import PropTypes from "prop-types";
import { uploadFileWeb } from "helpers/shared.helpers";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import LocationService from "services/Location.service";
import theme from "shared/assets/theme";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import CustomButton from "webComponentsFormElements/inputs/CustomButton.component";
import SearchBar from "webComponentsFormElements/inputs/SearchBar.component";
import LocationCard from "webComponentsScreens/locations/LocationCard.component";
import UpsertLocationForm from "webComponentsScreens/locations/UpsertLocationForm";
import Banner from "webComponentsShared/Banner.component";
import { ConfirmationContext } from "webContext/Confirmation.context";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";

const classes = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "white",
  },
  banner: {
    flexDirection: "row",
    gap: 20,
    alignItems: "center",
    paddingHorizontal: 10,
    paddingTop: 10,
  },
  scrollContainer: {
    flex: 1,
  },
  cardsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    padding: 10,
    gap: 10,
  },
  modalStyle: {
    width: "90%",
    maxWidth: "1200px",
    minHeight: "80vh",
  },
  noResultsText: {
    color: theme.color.darkGrey,
    marginTop: 20,
  },
});

export default function LocationScreen({ route, navigation }) {
  const { locationList, getLocations, getPersons, getOtherContacts } =
    useContext(OrganizationContext);
  const { showConfirmation } = useContext(ConfirmationContext);
  const { showToast } = useContext(ToastContext);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, openModal] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [locationToUpsert, setLocationToUpsert] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const effectiveSelectedLocationId =
    route.params?.locationId ?? selectedLocationId;
  const effectiveModalOpen = modalOpen || !!route.params?.locationId;
  const selectedLocation = useMemo(
    () =>
      locationList.find(
        (loc) => loc.locationId === effectiveSelectedLocationId,
      ) ?? null,
    [locationList, effectiveSelectedLocationId],
  );
  const modalTitle =
    selectedLocation?.name ?? (effectiveModalOpen ? "Create a Place" : "");

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      getLocations();
      getPersons();
      getOtherContacts();
      setIsLoading(false);
    };
    fetchData();
  }, [getLocations, getPersons, getOtherContacts]);

  const closeModal = useCallback(() => {
    openModal(false);
    setTimeout(() => {
      setSelectedLocationId(null);
      setSelectedImageFile(null);
      setPreviewImageUrl(null);
      setLocationToUpsert(null);
    }, 300);
    navigation.setParams({ locationId: undefined });
    setCanSave(false);
  }, [setCanSave, openModal, setSelectedLocationId, navigation]);

  const handleClose = useCallback(() => {
    canSave
      ? showConfirmation({
          prompt:
            "You have unsaved changes. Are you sure you want to discard them?",
          onConfirm: () => closeModal(),
        })
      : closeModal();
  }, [canSave, showConfirmation, closeModal]);

  const onDelete = (location) => {
    const people = location._associations?.persons || [];
    const dependencies =
      location._associations?.organizationSystemDependencies || [];
    showConfirmation({
      prompt: (
        <View style={{ alignItems: "center", gap: 20, padding: 10 }}>
          <Text>
            Are you sure you want to permanently delete the location "
            {location.name}"?
          </Text>
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 20,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {people.length > 0 && (
              <View
                style={{
                  gap: 10,
                  alignItems: "center",
                  flex: 1,
                  minWidth: 250,
                }}
              >
                <Text style={{ textAlign: "center" }}>
                  This action will unassign the following people:
                </Text>
                <ScrollView
                  style={{ maxHeight: 150, width: "80%" }}
                  contentContainerStyle={{
                    alignItems: "center",
                    paddingHorizontal: 10,
                  }}
                >
                  <View style={{ alignItems: "flex-start" }}>
                    {people.map((person) => (
                      <Text key={person.personId} style={{ fontSize: 15 }}>
                        • {person.firstName} {person.lastName}
                      </Text>
                    ))}
                  </View>
                </ScrollView>
              </View>
            )}
            {dependencies.length > 0 && (
              <View
                style={{
                  gap: 10,
                  alignItems: "center",
                  flex: 1,
                  minWidth: 250,
                }}
              >
                <Text style={{ textAlign: "center" }}>
                  and will remove the following dependencies for this location:
                </Text>
                <ScrollView
                  style={{ maxHeight: 150, width: "80%" }}
                  contentContainerStyle={{
                    alignItems: "center",
                    paddingHorizontal: 10,
                  }}
                >
                  <View style={{ alignItems: "flex-start" }}>
                    {dependencies.map((dependency) => (
                      <Text key={dependency.id} style={{ fontSize: 15 }}>
                        • {dependency.systemTitle}
                      </Text>
                    ))}
                  </View>
                </ScrollView>
              </View>
            )}
          </View>
        </View>
      ),
      onConfirm: () => handleLocationDelete(location),
    });
  };

  const handleLocationDelete = async (location) => {
    const res = await LocationService.deleteLocation(location.locationId);
    if (res.message && res.payload) {
      await getLocations();
    }
    showToast(`Successfully deleted ${location.name}.`, "success");
  };

  const handleSave = useCallback(async () => {
    if (locationToUpsert) {
      const upsertPayload = { ...locationToUpsert };

      if (selectedImageFile) {
        const res = await uploadFileWeb(selectedImageFile);

        upsertPayload.fileId = res?.fileId;
      } else if (previewImageUrl === null) {
        upsertPayload.fileId = null;
      }

      const res = await LocationService.upsertLocation(upsertPayload);
      if (res.status === 350) {
        showToast(res.message, "error");
        return;
      }
      if (res.message && res.payload) {
        await getLocations();
      }
      showToast(`Successfully saved ${upsertPayload.name}.`, "success");
    }

    closeModal();
    const fetchData = async () => {
      setIsLoading(true);
      getLocations();
      getPersons();
      getOtherContacts();
      setIsLoading(false);
    };
    await fetchData();
  }, [
    locationToUpsert,
    getLocations,
    getPersons,
    getOtherContacts,
    selectedImageFile,
    previewImageUrl,
    showToast,
    closeModal,
  ]);

  const filteredLocations = locationList.filter(
    (location) =>
      location.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.description?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return isLoading ? (
    <View>
      <ActivityIndicator size="large" />
    </View>
  ) : (
    <View style={classes.root}>
      <Banner classes={classes.banner}>
        <SearchBar
          placeholder="Search places..."
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        <CustomButton
          title="Add a Place"
          variant="dark"
          isLoading={isLoading}
          onPress={() => {
            openModal(true);
          }}
          icon="plus-circle-outline"
        />
      </Banner>
      <ScrollView
        style={classes.scrollContainer}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <View style={classes.cardsContainer}>
          {filteredLocations.map((location) => {
            return (
              <LocationCard
                key={location.locationId}
                location={location}
                onPress={() => {
                  setSelectedLocationId(location.locationId);
                  openModal(true);
                }}
                onDelete={() => onDelete(location)}
              />
            );
          })}
          {filteredLocations.length === 0 && (
            <Text style={classes.noResultsText}>
              {searchQuery
                ? "No places match your search."
                : "No places found."}
            </Text>
          )}
        </View>
      </ScrollView>
      <CustomModal
        title={modalTitle}
        open={effectiveModalOpen}
        canSave={canSave}
        onSave={() => handleSave()}
        onClose={() => {
          handleClose();
        }}
        scroll
        style={classes.modalStyle}
      >
        <UpsertLocationForm
          key={effectiveSelectedLocationId ?? "new"}
          setCanSave={setCanSave}
          location={selectedLocation}
          locationToUpsert={locationToUpsert}
          setLocationToUpsert={setLocationToUpsert}
          locationList={locationList}
          setSelectedImageFile={setSelectedImageFile}
          previewImageUrl={previewImageUrl}
          setPreviewImageUrl={setPreviewImageUrl}
          onClose={handleClose}
        />
      </CustomModal>
    </View>
  );
}

  LocationScreen.propTypes = {
    route: PropTypes.shape({
      params: PropTypes.shape({
        locationId: PropTypes.array,
      }),
    }),
  };